import { AuthContext } from "../Context/AuthContext";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import { TextField } from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import AppleLogin from "react-apple-login";
import axiosInstance from '../utils/axiosConfig';
import Navbar from "../components/Navbar";
import TenantContext from "../Context/TenantContext.jsx";
import axios from 'axios';
import { FiCopy, FiCheck } from "react-icons/fi";
import { FaApple } from "react-icons/fa";

const Login = () => {
  const { isAuthenticated, setIsAuthenticated, setUser, setToken, login } = useContext(AuthContext);
  const { tenant = { name: 'Smart Nurse', logo: '/logo.png', branding: { 'qr-logo': '/qr-logo.png' }, urls: { webLoginUrl: 'https://bh.healgpt.co/login' } } } = useContext(TenantContext) || {};
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [copiedWebUrl, setCopiedWebUrl] = useState(false);
  const webLoginUrl = tenant?.urls?.webLoginUrl || "https://bh.healgpt.co/login";

  const handleCopyWebUrl = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(webLoginUrl);
        setCopiedWebUrl(true);
        setTimeout(() => setCopiedWebUrl(false), 1500);
      }
    } catch (e) {
      console.error("Copy failed", e);
    }
  };

  const navigateTo = useNavigate();

  const googleLogin = useGoogleLogin({
    onSuccess: (tokenResponse) => handleGoogleCode(tokenResponse.code),
    flow: "auth-code",
    // When using popup flow, the standard redirect_uri is "postmessage"
    // Do not use window.location.origin for popup flow
    redirect_uri: "postmessage",
    scope: "email profile openid https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email",
  });

  useEffect(() => {
    // Extra logging to debug AppleID SDK loading
    console.log("[Apple SSO] window.AppleID:", window.AppleID);
    if (!window.AppleID) {
      console.error("[Apple SSO] AppleID SDK not loaded. Check index.html script tag.");
      return;
    }
    setTimeout(() => {
      try {
        window.AppleID.auth.init({
          clientId: import.meta.env.VITE_APP_APPLE_CLIENTID,
          redirectURI: import.meta.env.VITE_APP_APPLE_REDIRECT_URL,
          usePopup: true,
        });
        console.log("[Apple SSO] AppleID.auth.init called");
      } catch (e) {
        console.error("[Apple SSO] Error initializing AppleID:", e);
      }
    }, 500);
  }, []);

  /**
   *
   * @param {string} code
   */
  async function handleGoogleCode(code) {
    try {
      const requestData = { 
        code, 
        role: "Patient",
        redirect_uri: "postmessage", // Always use postmessage for popup flow
        organization: import.meta.env.VITE_TENANT_OVERRIDE || 'vikramaura-hospitals' // Send tenant organization
      };
      
      console.log("[Google SSO] Sending request data:", requestData);
      console.log("[Google SSO] AxiosInstance baseURL:", axiosInstance.defaults.baseURL);
      console.log("[Google SSO] Request URL will be:", axiosInstance.defaults.baseURL + "/user/google/callback");
      
      // Use raw axios for Google callback to avoid auth interceptor
      const [data, axiosError] = await axios
        .post(
          `${import.meta.env.VITE_APP_BASE_API}${import.meta.env.VITE_APP_API_VERSION}/user/google/callback`,
          requestData,
          {
            withCredentials: true,
            headers: { "Content-Type": "application/json" },
          }
        )
        .then(({ data }) => [data, undefined])
        .catch((err) => [undefined, err]);

      if (axiosError) {
        // Handle network errors (like SSL errors) where response is undefined
        if (!axiosError.response) {
          console.error("[Google SSO] Network error:", axiosError.message);
          toast.error("Network error: Unable to connect to server. Please check your connection.");
          return;
        }

        if (axiosError.response.status === 403) {
          navigateTo("/sso-onboarding", {
            state: {
              ...axiosError.response.data.data,
              ssoType: "google", // Add SSO type identifier
            },
          });
          toast.info(axiosError.response.data.message);
          return;
        }

        // Handle other HTTP errors
        const errorMessage = axiosError.response?.data?.message || axiosError.message || "Authentication failed";
        toast.error(errorMessage);
        return;
      }

      if (data.new_user === true) {
        navigateTo("/sso-onboarding", {
          state: {
            email: data?.data?.email,  // Back to "data" since backend now uses consistent structure
            firstName: data?.data?.firstName,
            lastName: data?.data?.lastName,
            ssoType: "google", // Add SSO type identifier
          },
        });
        return;
      }

      if (!data.user || !data.token) {
        console.error("[Google SSO] Missing user or token in response:", data);
        toast.error("Sign-in failed: incomplete response from server");
        return;
      }
      toast.success(data.message);
      setIsAuthenticated(true);
      setUser(data.user);
      setToken(data.token);
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      if (data.expiresAt) {
        localStorage.setItem('tokenExpiresAt', data.expiresAt);
      }
      navigateTo("/");
      setEmail("");
      setPassword("");
    } catch (err) {
      console.error(err);
      toast.error("Some Error Occured");
    }
  }

  async function appleResponse(response) {
    console.log("[Apple SSO] Apple response received:", response);
    if (response.error) {
      // Handle popup closed by user gracefully
      if (response.error.error === 'popup_closed_by_user') {
        toast.info("Apple sign-in was cancelled. Please try again and complete the sign-in in the popup window.");
        return;
      }
      console.error("[Apple SSO] Apple sign-in error:", response.error);
      toast.error("Problem with Apple sign-in: " + response.error.error);
      return;
    }
    if (!response.authorization?.id_token) {
      console.error("[Apple SSO] No ID token in Apple response", response);
      toast.error("Apple sign-in failed: No authentication token received");
      return;
    }
    try {
      // Use raw axios for Apple callback to avoid auth interceptor
      const [data, error] = await axios
        .post(`${import.meta.env.VITE_APP_BASE_API}${import.meta.env.VITE_APP_API_VERSION}/user/apple/callback`, {
          id_token: response.authorization.id_token,
          firstName: response?.user?.name?.firstName,
          lastName: response?.user?.name?.lastName,
          email: response?.user?.email,
          role: "Patient",
        })
        .then((res) => [res, undefined])
        .catch((err) => [undefined, err]);
      if (error) {
        console.error("[Apple SSO] Apple callback error:", error.response?.data || error.message);
        if (error.response?.status === 403) {
          navigateTo("/sso-onboarding?appleSub=" + error.response?.data?.data?.appleSub, {
            state: {
              ...error.response.data.data,
              ssoType: "apple", // Add SSO type identifier
            },
          });
          toast.info(error.response.data.message);
          return;
        }
        const errorMessage = error.response?.data?.message || error.message || "Apple sign-in failed";
        toast.error(errorMessage);
        return;
      }
      console.log("[Apple SSO] Apple callback success:", data.data);
      if (data.data.new_user === true) {
        if (!data.data.data.email) {
          navigateTo("/sso-onboarding?appleSub=" + data.data.data.appleSub);
          return;
        }
        navigateTo("/sso-onboarding", {
          state: {
            email: data.data.data?.email,
            firstName: data.data.data?.firstName,
            lastName: data.data.data?.lastName,
            appleSub: data.data.data?.appleSub,
            ssoType: "apple", // Add SSO type identifier
          },
        });
        return;
      }
      if (!data.data.user || !data.data.token) {
        console.error("[Apple SSO] Missing user or token in response:", data.data);
        toast.error("Sign-in failed: incomplete response from server");
        return;
      }
      toast.success(data.data.message);
      setIsAuthenticated(true);
      setUser(data.data.user);
      setToken(data.data.token);
      localStorage.setItem('token', data.data.token);
      localStorage.setItem('user', JSON.stringify(data.data.user));
      if (data.data.expiresAt) {
        localStorage.setItem('tokenExpiresAt', data.data.expiresAt);
      }
      navigateTo("/");
      setEmail("");
      setPassword("");
    } catch (err) {
      console.error("[Apple SSO] Unexpected Apple sign-in error:", err);
      toast.error("An unexpected error occurred during Apple sign-in");
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Use the dedicated patient login endpoint
      const response = await axios.post(
        `${import.meta.env.VITE_APP_BASE_API}${import.meta.env.VITE_APP_API_VERSION}/user/patient/login`,
        { email, password }
      );
      
      const { success, message, user, token, redirectUrl, expiresAt } = response.data;
      
      if (success) {
        toast.success(message);
        
        // Store the expiration time
        if (expiresAt) {
          localStorage.setItem('tokenExpiresAt', expiresAt);
        }
        
        // Use the context login function to set user and token
        login(user, token);
        
        // Navigate to the appropriate page
        navigateTo(redirectUrl || '/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.message || 'Login failed. Please try again.');
    }
  };

  const submitForgotPassword = async (e) => {
    e.preventDefault();
    try {
      // Use raw axios for forgot password to avoid auth interceptor
      const { data } = await axios.post(
        `${import.meta.env.VITE_APP_BASE_API}${import.meta.env.VITE_APP_API_VERSION}/user/forgot-password`,
        { email: forgotEmail },
        { withCredentials: true }
      );
      toast.success(data.message);
      setShowForgotPassword(false);
    } catch (error) {
      toast.error(error.response.data.message);
    }
  };

  if (isAuthenticated) {
    return <Navigate to={"/"} />;
  }

  return (
    <>
  <Navbar />
      <div className="py-8 flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 px-4">
        <div className="w-full max-w-md space-y-2">
          {/* Logo and Title Section */}
          <div className="text-center">
    <img src={tenant.logo || "/logo.png"} alt={`${tenant.name || 'Smart Nurse'} logo`} className="h-14 mx-auto mb-2" onError={(e)=>{ e.currentTarget.src='/logo.png'; }} />
    <h2 className="text-3xl font-semibold text-gray-900 mb-2">Sign in with your account</h2>
    <p className="text-gray-600">{tenant.name || 'Smart Nurse'}</p>
          </div>

          {/* Login Form */}
          <div className="bg-white py-8 px-4 shadow-2xl rounded-2xl space-y-8 sm:px-10">
            {/* Social Login Buttons */}
            <div className="space-y-4">
              <button
                onClick={googleLogin}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>

              {import.meta.env.VITE_APP_APPLE_CLIENTID && (
              <AppleLogin
                clientId={import.meta.env.VITE_APP_APPLE_CLIENTID}
                redirectURI={import.meta.env.VITE_APP_APPLE_REDIRECT_URL}
                usePopup={true}
                callback={appleResponse}
                scope="email name"
                responseMode="form_post"
                render={(renderProps) => (
                  <button
                    onClick={renderProps.onClick}
                    className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-300 rounded-full text-gray-700 bg-white hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    <FaApple className="w-5 h-5" />
                    Continue with Apple
                  </button>
                )}
              />
              )}
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500">Or</span>
              </div>
            </div>

            {/* Email Password Form */}
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <TextField
                  fullWidth
                  label="Email address"
                  variant="outlined"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="rounded-full"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                    },
                  }}
                />
              </div>

              <div>
                <TextField
                  fullWidth
                  label="Password"
                  variant="outlined"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                    },
                  }}
                />
              </div>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  Forgot password?
                </button>
                <Link
                  to="/register"
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  Create new account
                </Link>
              </div>

              <button
                type="submit"
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                Sign in
              </button>
            </form>

            {/* Web Login QR Code */}
            <div className="mt-6 rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-gray-50 p-5 shadow-sm">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Web Login</h3>
                  <p className="text-sm text-gray-500">Scan to open the web login page</p>
                </div>
                <button
                  type="button"
                  onClick={handleCopyWebUrl}
                  className="inline-flex items-center gap-2 rounded-full border border-gray-200 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-50"
                  aria-label="Copy web login link"
                >
                  {copiedWebUrl ? (
                    <>
                      <FiCheck className="text-green-600" /> Copied
                    </>
                  ) : (
                    <>
                      <FiCopy /> Copy link
                    </>
                  )}
                </button>
              </div>

              <div className="mt-4 flex justify-center">
                <div className="relative rounded-xl bg-white p-3 border border-gray-200 shadow-inner">
                  <img
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=300x300&ecc=H&margin=20&data=${encodeURIComponent(webLoginUrl)}`}
                    alt="QR code for web login"
                    className="h-48 w-48 block"
                    loading="lazy"
                    decoding="async"
                  />
                  {/* Center logo with a white mask to keep QR scannable */}
                  <div
                    className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-md flex items-center justify-center"
                    style={{ width: '48px', height: '48px' }}
                    aria-hidden="true"
                  >
                    <img
                      src={tenant.branding?.['qr-logo'] || '/qr-logo.png'}
                      alt=""
                      className="h-8 w-8 select-none"
                      draggable="false"
                      role="presentation"
                    />
                  </div>
                </div>
              </div>
              <div className="mt-4 text-center">
                <a
                  href={webLoginUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-blue-600 hover:underline break-all"
                >
                  {webLoginUrl}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <>
          {/* Backdrop with blur effect */}
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm transition-opacity" />
          
          <div className="fixed inset-0 z-50 overflow-y-auto overflow-x-hidden flex justify-center items-center w-full md:inset-0 h-[calc(100%-1rem)] max-h-full">
            <div className="relative p-4 w-full max-w-md max-h-full">
              {/* Modal content with acrylic effect */}
              <div className="relative bg-white/70 backdrop-blur-md rounded-2xl shadow-xl border border-white/20">
                {/* Modal header */}
                <div className="flex items-center justify-between p-4 md:p-5 border-b border-gray-200/50">
                  <h3 className="text-xl font-semibold text-gray-900">
                    Reset Password
                  </h3>
                  <button
                    type="button"
                    className="end-2.5 text-gray-400 bg-transparent hover:bg-gray-200/50 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center transition-colors"
                    onClick={() => setShowForgotPassword(false)}
                  >
                    <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
                    </svg>
                    <span className="sr-only">Close modal</span>
                  </button>
                </div>
                {/* Modal body */}
                <div className="p-4 md:p-5">
                  <form onSubmit={submitForgotPassword} className="space-y-4">
                    <div>
                      <label htmlFor="email" className="block mb-2 text-sm font-medium text-gray-900">
                        Your email
                      </label>
                      <input
                        type="email"
                        id="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="bg-white/50 backdrop-blur-sm border border-gray-200 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                        placeholder="name@gmail.com"
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      className="w-full text-white bg-blue-600/90 hover:bg-blue-700 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center backdrop-blur-sm transition-colors"
                    >
                      Send Reset Link
                    </button>
                    <div className="text-sm font-medium text-gray-600">
                      Remember your password? <button
                        type="button"
                        onClick={() => setShowForgotPassword(false)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        Sign in
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Login;
